import type { FC, ReactNode } from 'react'
import type { LegalPage } from '@/types'

import Link from 'next/link'
import { MaskLines } from '@/components/motion/reveal'
import { ToneSection } from '@/components/generals/tone'
import { legalLinks, siteConfig } from '@/constants/site'
import RichText from '@/components/generals/rich-text'

interface LegalViewProps {
    page: LegalPage | null
    fallbackTitle: string
    current: string
}

export const LegalView: FC<LegalViewProps> = ({ page, fallbackTitle, current }): ReactNode => {
    return (
        <ToneSection tone='paper' id='legal' className='px-5 pb-28 pt-32 md:px-8 lg:px-12 lg:pb-40 lg:pt-44'>
            <div className='grid gap-12 lg:grid-cols-12'>
                <aside className='lg:col-span-3'>
                    <nav aria-label='Legal' className='flex flex-col gap-3 lg:sticky lg:top-32'>
                        <span className='t-label text-tone-muted'>
                            Legal
                        </span>
                        {legalLinks.map(link => (
                            <Link
                                key={link.href}
                                href={link.href}
                                aria-current={link.href === current ? 'page' : undefined}
                                className={link.href === current ? 'font-semibold text-tone' : 'text-tone-muted transition-colors hover:text-tone'}
                            >
                                {link.label}
                            </Link>
                        ))}
                        <a href={`mailto:${siteConfig.email}`} className='mt-6 text-sm text-tone-muted underline underline-offset-4'>
                            Questions? {siteConfig.email}
                        </a>
                    </nav>
                </aside>

                <div className='lg:col-span-8 lg:col-start-5'>
                    {page ? (
                        <h1
                            data-mdlfy-cms={`legal_pages:${page.id}:title`}
                            className='font-heading text-[clamp(2.6rem,6vw,6rem)] font-semibold uppercase leading-[0.9] tracking-[-0.04em] wdth-75'
                        >
                            {page.title}
                        </h1>
                    ) : (
                        <MaskLines
                            as='h1'
                            immediate
                            className='font-heading text-[clamp(2.6rem,6vw,6rem)] font-semibold uppercase leading-[0.9] tracking-[-0.04em] wdth-75'
                            lines={[fallbackTitle]}
                        />
                    )}

                    <div className='mt-14 border-t border-tone-line pt-12'>
                        {page?.body
                            ? <RichText html={page.body} className='max-w-3xl text-[1.0625rem] leading-[1.75] [&_a]:text-tone-accent' />
                            : (
                                <p className='text-tone-muted'>
                                    This page is being updated. For questions, write to{' '}
                                    <a href={`mailto:${siteConfig.email}`} className='underline underline-offset-4'>
                                        {siteConfig.email}
                                    </a>
                                    .
                                </p>
                            )}
                    </div>
                </div>
            </div>
        </ToneSection>
    )
}
